import React from 'react';
import { Link } from 'react-router-dom';
import AccentText from '../storefront/AccentText';
import { copyHasTokens, fillCopy } from '../storefront/homepageCopy';
import { IconArrowRight, Longhorn } from '../storefront/icons';

// Warm backgrounds the product cut-outs are multiplied onto, cycled per tile.
const TILE_TONES = ['#ebe3d6', '#e6dccd', '#efe8dd', '#e7d9c9', '#e3dace'];
const FEATURE_SUBS = 4;
const pad = (n) => String(n).padStart(2, '0');

// Desktop 12-column spans: the first collection is a 6×2 feature, the next
// four fill a 2×2 block beside it, then rows of four. A short final row
// stretches to fill the width, so any number of featured collections tiles
// cleanly.
const desktopSpans = (count) => {
  if (count === 0) return [];
  const spans = [{ col: 6, row: 2 }];
  const beside = Math.min(count - 1, 4);
  const besideLayouts = {
    1: [{ col: 6, row: 2 }],
    2: [{ col: 6, row: 1 }, { col: 6, row: 1 }],
    3: [{ col: 6, row: 1 }, { col: 3, row: 1 }, { col: 3, row: 1 }],
    4: [{ col: 3, row: 1 }, { col: 3, row: 1 }, { col: 3, row: 1 }, { col: 3, row: 1 }],
  };
  spans.push(...(besideLayouts[beside] || []));

  const rest = count - 1 - beside;
  for (let i = 0; i < rest; i += 1) {
    const remainingInRow = Math.min(4, rest - Math.floor(i / 4) * 4);
    spans.push({ col: 12 / remainingInRow, row: 1 });
  }
  return spans;
};

const CollectionTile = ({ collection, index, span }) => {
  const isFeature = index === 0;
  const count = collection.children.length;

  return (
    <Link
      to={collection.to}
      aria-label={count > 0 ? `${collection.title}, ${count} categories` : collection.title}
      className={`tp-tile${isFeature ? ' tp-tile--feature' : ''}`}
      style={{
        '--tone': TILE_TONES[index % TILE_TONES.length],
        '--col': span.col,
        '--row': span.row,
        '--reveal-delay': Math.min(index, 6) * 70,
      }}
      data-reveal
    >
      <span className="tp-tile__media">
        {collection.image ? (
          <img className="tp-tile__img" src={collection.image} alt="" loading="lazy" decoding="async" />
        ) : (
          <Longhorn className="tp-tile__glyph" />
        )}
      </span>

      <span className="tp-tile__top">
        <span className="tp-tile__index">{pad(index + 1)}</span>
        {count > 0 && <span className="tp-tile__count">{count} categories</span>}
      </span>

      <span className="tp-tile__body">
        <span className="tp-tile__text">
          <span className="tp-tile__title">{collection.title}</span>
          {isFeature && count > 0 && (
            <span className="tp-tile__subs">
              {collection.children.slice(0, FEATURE_SUBS).map((child) => (
                <span key={child.id} className="tp-tile__sub">{child.name}</span>
              ))}
              {count > FEATURE_SUBS && <span className="tp-tile__sub">+{count - FEATURE_SUBS} more</span>}
            </span>
          )}
        </span>
        <span className="tp-tile__go" aria-hidden="true">
          <IconArrowRight size={18} />
        </span>
      </span>
    </Link>
  );
};

const CollectionsMosaic = ({ collections, loading, totalCollections, totalCategories, copy }) => {
  const spans = desktopSpans(collections.length);
  const body = copy['collections.body'];
  // Hold back a paragraph that quotes the counts until they've loaded.
  const showBody = totalCollections > 0 || !copyHasTokens(body, ['collections', 'categories']);

  return (
    <section className="tp-section tp-collections" aria-labelledby="tp-collections-title">
      <div className="tp-container">
        <header className="tp-section-head" data-reveal>
          <div>
            <span className="tp-eyebrow" data-copy-key="collections.eyebrow">{copy['collections.eyebrow']}</span>
            <h2 id="tp-collections-title" className="tp-display tp-section-head__title" data-copy-key="collections.title">
              <AccentText text={copy['collections.title']} />
            </h2>
          </div>
          <div className="tp-section-head__aside">
            {showBody && (
              <p data-copy-key="collections.body">{fillCopy(body, { collections: totalCollections, categories: totalCategories })}</p>
            )}
            <Link to="/products" className="tp-textlink" data-copy-key="collections.link">
              {copy['collections.link']} <IconArrowRight size={16} />
            </Link>
          </div>
        </header>

        {loading ? (
          <div className="tp-mosaic" aria-hidden="true">
            {desktopSpans(9).map((span, i) => (
              <div
                key={i}
                className="tp-tile tp-tile--skeleton tp-skeleton"
                style={{ '--col': span.col, '--row': span.row }}
              />
            ))}
          </div>
        ) : (
          <div className="tp-mosaic">
            {collections.map((collection, index) => (
              <CollectionTile
                key={collection.id}
                collection={collection}
                index={index}
                span={spans[index]}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CollectionsMosaic;
